import { useCallback } from 'react';
import { usePdfHandler } from './usePdfHandler';
import { usePdfState } from './usePdfState';
import { getUrlFromBase64PdfString } from '../utils';
import { getGlobalLogger } from '@zg-rentals/logger-browser';
import type { PdfHookCommonProps } from './types';

export type UsePdfFromRequester<T> = PdfHookCommonProps & {
  onError?: (err: Error) => void;
  onSuccess?: (base64String: string) => void;
  requester: (getParams: T) => Promise<string>;
};

export function usePdfFromRequester<T>({
  downloadFilename,
  onError,
  onSuccess,
  openInNewTab = false,
  requester,
}: UsePdfFromRequester<T>) {
  const { pdfUrl, setPdfUrl, isLoading, setIsLoading } = usePdfState();

  const getPdf = useCallback(
    async (getParams: T) => {
      setIsLoading(true);
      try {
        const res = await requester(getParams);
        const url = await getUrlFromBase64PdfString(res);
        setPdfUrl(url);
        onSuccess?.(res);
      } catch (err) {
        getGlobalLogger('usePdfFromRequester').error(err);

        if (onError) {
          onError(err);
        } else {
          throw err;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onError, requester, setIsLoading, setPdfUrl, onSuccess],
  );

  usePdfHandler({ pdfUrl, openInNewTab, downloadFilename });

  return { getPdf, isLoading, pdfUrl };
}
